// Block / section visibility evaluator.
//
// `BlockVisibility` (devices / roles / schedule / condition) is authored in
// the designer but was never evaluated at render time — blocks showed
// regardless. This is the shared gate used by both the smart-page runtime
// (RuntimeBlockHost / section render) and the public SSR prerenderer, so
// authored visibility rules actually take effect everywhere.

import type { BlockVisibility, Viewport } from '@twasta/page-schema'
import { substituteVars, type VarSource } from './actions/substitute'
import type { AuthMenuAdapter } from './renderers'

export interface VisibilityCtx {
  /** Current viewport; when omitted, device rules are not enforced. */
  viewport?: Viewport
  /**
   * Current user's roles. `undefined` means "unknown" — role rules are
   * skipped (fail-open) so a runtime that hasn't wired auth doesn't hide
   * everything. Pass `[]` for an anonymous viewer (public SSR) to enforce
   * role rules (role-gated blocks become hidden).
   */
  roles?: string[]
  /** Variable source for `{{condition}}` evaluation. */
  vars?: VarSource
  /** Clock for schedule rules (defaults to now). */
  now?: Date
}

/**
 * Returns true when a block/section with this `visibility` should render in
 * the given context. No rules (or undefined) → always visible.
 */
export function isVisible(v: BlockVisibility | undefined, ctx: VisibilityCtx = {}): boolean {
  if (!v) return true

  // Device — only when the caller knows the current viewport.
  if (v.devices && v.devices.length > 0 && ctx.viewport) {
    if (!v.devices.includes(ctx.viewport)) return false
  }

  // Roles — only enforced when the caller supplies roles (anon = []).
  if (v.roles && v.roles.length > 0 && ctx.roles !== undefined) {
    const allowed = new Set(v.roles)
    if (!ctx.roles.some((r) => allowed.has(r))) return false
  }

  // Schedule window (ISO timestamps).
  if (v.schedule) {
    const now = (ctx.now ?? new Date()).getTime()
    if (v.schedule.start) {
      const s = Date.parse(v.schedule.start)
      if (!Number.isNaN(s) && now < s) return false
    }
    if (v.schedule.end) {
      const e = Date.parse(v.schedule.end)
      if (!Number.isNaN(e) && now > e) return false
    }
  }

  // Condition — `{{var}}` expression. Fail-open on error so a bad
  // expression never silently hides content.
  if (v.condition && v.condition.trim()) {
    if (!evalCondition(v.condition, ctx.vars ?? {})) return false
  }

  return true
}

const SAFE_EXPR = /^[\s0-9"'.\-+*/%<>=!&|()[\]_a-zA-Z,]+$/

// A condition token becomes a LITERAL, so `{{status}} == 'PENDING'` compares
// strings instead of throwing on a bare word (which used to fail OPEN — every
// such condition was silently true). 'true'/'false', numbers and null go in
// as themselves; any other text is quoted. A token the author already put
// inside quotes (`'{{status}}' == 'PENDING'`) is spliced in raw, as before,
// and an unresolved token is left as written (fail-open below).
const COND_TOKEN_RE = /(['"]?)\{\{\s*([a-zA-Z0-9_.[\]]+)\s*(\|\s*[a-z_]+\s*)?\}\}(['"]?)/g

function conditionLiteral(raw: string): string {
  const t = raw.trim()
  if (t === 'true' || t === 'false' || t === 'null') return t
  // A leading zero stays text ("0123" is a PIN, not legacy octal 83).
  if (/^-?(0|[1-9]\d*)(\.\d+)?$/.test(t)) return t
  return JSON.stringify(raw)
}

function substituteCondition(expr: string, vars: VarSource): string {
  return expr.replace(COND_TOKEN_RE, (match, open: string, key: string, filter: string | undefined, close: string) => {
    const token = filter ? `{{${key}|${filter.replace(/[|\s]/g, '')}}}` : `{{${key}}}`
    const value = substituteVars(token, vars)
    if (value === token) return match                 // unresolved: as written
    // Inside the author's own quotes: escaped, so a value can never close the
    // string and run as code.
    if (open && open === close) return open + value.replace(/\\/g, '\\\\').split(open).join('\\' + open) + close
    return open + conditionLiteral(value) + close
  })
}

export function evalCondition(expr: string, vars: VarSource): boolean {
  const subbed = substituteCondition(expr, vars).trim()
  if (!subbed) return true
  if (subbed === 'true') return true
  if (subbed === 'false' || subbed === '0' || subbed === 'null' || subbed === 'undefined') return false
  // String literals are data, so the character whitelist is applied with
  // them blanked out — an e-mail or a dash in a value must not turn the
  // condition into "always visible".
  const shape = subbed.replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g, '""')
  if (SAFE_EXPR.test(shape)) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
      return Boolean(new Function(`return (${subbed});`)())
    } catch {
      return true  // fail-open
    }
  }
  // Non-evaluable leftover (e.g. an unresolved token) → visible if non-empty.
  return subbed.length > 0
}

// `{{me.*}}` — the signed-in visitor, as page variables.
//
// Every live surface already hands the session to the `auth_menu` through an
// `AuthMenuAdapter`; this exposes the same facts to page TEXT and to
// visibility conditions, so a page can say "Applying as {{me.email}}" or show
// a block only when `{{me.signedIn}}` — with no surface-specific code and no
// value the author has to type. Surfaces without a session (the static
// prerender, the designer canvas) see a signed-out visitor.
//
//   me.signedIn   'true' | 'false'   (strings, so a condition reads them as-is)
//   me.signedOut  'true' | 'false'
//   me.name       display name ('' when signed out)
//   me.email      e-mail ('' when signed out or not known)
//   me.roles      comma-separated role names

export interface MeVars {
  signedIn: 'true' | 'false'
  signedOut: 'true' | 'false'
  name: string
  email: string
  roles: string
}

export function meOf(adapter: AuthMenuAdapter | null | undefined): MeVars {
  const user = adapter && adapter.status === 'authenticated' ? adapter.user : null
  if (!user) return { signedIn: 'false', signedOut: 'true', name: '', email: '', roles: '' }
  return {
    signedIn: 'true', signedOut: 'false',
    name: user.username || '',
    email: user.email || '',
    roles: (user.roles || []).join(','),
  }
}

/** `vars` with `me` (and `me.<field>`) resolved from the session. A page
 *  variable the author named `me` wins, so nothing existing changes meaning. */
export function withMe(vars: VarSource | undefined, me: MeVars): VarSource {
  const base = vars || {}
  return (key: string) => {
    const own = typeof base === 'function'
      ? base(key)
      : (Object.prototype.hasOwnProperty.call(base, key) ? (base as Record<string, unknown>)[key] : undefined)
    if (own !== undefined) return own
    if (key === 'me') return me
    if (key.startsWith('me.')) {
      const f = key.slice(3) as keyof MeVars
      return Object.prototype.hasOwnProperty.call(me, f) ? me[f] : undefined
    }
    return undefined
  }
}

/**
 * Device rules as CSS. `isVisible` can only enforce `devices` when the caller
 * knows the viewport — and the published site and the live viewer do not
 * (one HTML file serves every screen), so a block marked "mobile only" or
 * "hide on mobile" showed everywhere. These classes hide it by @media
 * instead; `DEVICE_VISIBILITY_CSS` (in `pageRuntimeCss`) holds the rules.
 * Breakpoints are RESPONSIVE_BREAKPOINTS': mobile ≤640, tablet ≤1024.
 * The lower bounds sit at +0.02px, not +1px: a zoomed or scaled screen can be
 * 640.5px wide, and with `min-width:641px` no rule matched there, so a
 * device-restricted block showed on the wrong device.
 */
export function deviceVisibilityClass(v: BlockVisibility | undefined): string | undefined {
  const d = v?.devices
  if (!d || d.length === 0) return undefined
  const out = (['desktop', 'tablet', 'mobile'] as Viewport[])
    .filter((vp) => !d.includes(vp))
    .map((vp) => `pg-hide-${vp}`)
  return out.length ? out.join(' ') : undefined
}

export const DEVICE_VISIBILITY_CSS = `
@media (min-width:1024.02px){.pg-hide-desktop{display:none!important}}
@media (min-width:640.02px) and (max-width:1024px){.pg-hide-tablet{display:none!important}}
@media (max-width:640px){.pg-hide-mobile{display:none!important}}
`
